import { Box, Button } from '@mui/material'

import { useAutoScroll } from '@/atoms'
import { LayoutComposer, ListFooter, NoEntries, NoResults } from '@/components/List'
import { StructuredVirtualList } from '@/components/StructuredVirtualList'
import { ComponentSize, FormColumn, ListOverlayEditCover } from '@/components/layout'
import { ReadOnlyTooltip } from '@/components/smartUtils/plan/ReadOnlyTooltip'

import { Item } from './Item'
import { Header } from './components/Header'
import { useDataForList } from './hooks/useDataForList'
import { useListHandlers } from './hooks/useListHandlers'
import { useTexts } from './hooks/useTexts'

interface Props {
  regions: Record<string, uui.domain.client.rm.Region>
  selection: workwave.DeepReadonly<string[]>
  editing: boolean
}

const getItemHeight = () => 78

export function List(props: Props) {
  const { regions, selection, editing } = props

  const { handleOnSelect, handleOnSelectAll, handleOnDoubleClick, handleOnClick } =
    useListHandlers(selection)
  const { structure, lastSelectedRegion } = useDataForList()

  const texts = useTexts()

  const allSelected = selection.length >= structure.shownItems
  const multiSelect = selection.length > 1

  useAutoScroll('regions', lastSelectedRegion)

  return (
    <FormColumn width={320} testid="form__column-primary">
      <Header />

      <Box width="100%" height="100%" flex="0 1 auto" position="relative">
        <LayoutComposer
          totalItems={structure.totalItems}
          shownItems={structure.shownItems}
          noResults={
            <NoResults title={texts.noResults.title} description={texts.noResults.description} />
          }
          noEntries={
            <NoEntries
              title={texts.noEntries.title}
              description={texts.noEntries.description}
              action={
                <ReadOnlyTooltip
                  render={preventEditing => (
                    <Button
                      size="large"
                      variant="outlined"
                      onClick={handleOnClick}
                      disabled={preventEditing || editing}
                      data-testid="no-entries-add-item"
                      data-trackid="no-entries-add-item"
                    >
                      {texts.noEntries.action}
                    </Button>
                  )}
                />
              }
            />
          }
          list={
            <ComponentSize
              render={size => {
                // TODO: reset the scroll when the list options change
                return (
                  <>
                    <StructuredVirtualList
                      itemsById={regions}
                      structure={structure}
                      listApiCategory="regions"
                      getItemHeights={getItemHeight}
                      RenderItem={Item}
                      listHeight={size.height}
                      selectedItemIds={selection}
                      onSelect={handleOnSelect}
                      onDoubleClick={handleOnDoubleClick}
                      buffer={50}
                    />
                    {editing && <ListOverlayEditCover />}
                  </>
                )
              }}
            />
          }
        />
      </Box>

      {multiSelect && (
        <ListFooter
          label={texts.selectBtn(allSelected)}
          onToggleSelection={handleOnSelectAll}
          checked={allSelected}
        />
      )}
    </FormColumn>
  )
}
